import { faExclamationTriangle, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useEntryFormLogic } from 'hooks/useEntryFormLogic';
import { useForm } from 'hooks/useForm';
import { EntryFormProvider } from 'context/EntryFormContext';
import { createEntry, updateEntry, clearError } from '@redux/features/entriesSlice';
import { Button } from 'components/Button';
import { getEntrySchema } from 'constants/formValidationSchemas/entrySchema';
import { EntryTypeSelector } from './components/EntryTypeSelector';
import { TimeRangePicker } from './components/TimeRangePicker';
import { HallSelector } from './components/HallSelector';
import { ClientFields } from './components/ClientFields';
import { CommentInput } from './components/CommentInput';
import { CoachFields } from './components/CoachFields';
import { GroupInput } from './components/GroupInput';
import styles from 'pages/Schedule/CreateEntry/CreateEntry.scss';

export const CreateEntry = ({
  allClients,
  allCoaches,
  groups,
  currentDate,
  locationID,
  halls,
  setIsOpen,
  editingEntry = null,
}) => {
  const { error } = useSelector((state) => state.entries);
  const dispatch = useDispatch();
  const isEditing = Boolean(editingEntry);

  useEffect(() => {
    dispatch(clearError());
  }, []);

  const {
    initialValues,
    transformSubmitData,
    entryTypeIndex,
    setEntryTypeIndex,
    hallIndex,
    setHallIndex,
    currentLocationGroups,
  } = useEntryFormLogic({
    currentDate,
    locationID,
    halls,
    groups,
    editingEntry,
    allClients,
    allCoaches,
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const transformedData = transformSubmitData(values);

      const action = isEditing
        ? updateEntry({ ...transformedData, id: editingEntry.id })
        : createEntry(transformedData);

      const result = await dispatch(action);

      if (result.meta?.requestStatus === 'fulfilled') {
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const form = useForm({
    initialValues,
    validationSchema: getEntrySchema(entryTypeIndex),
    onSubmit: handleSubmit,
  });

  const contextValue = {
    form,
    entryTypeIndex,
    setEntryTypeIndex,
    hallIndex,
    setHallIndex,
    halls,
    allClients,
    allCoaches,
    currentLocationGroups,
    isEditing,
    currentDate,
  };

  return (
    <EntryFormProvider value={contextValue}>
      <form className={styles.form} onSubmit={form.handleSubmit}>
        <div className={styles.formRow}>
          <h3 className={styles.title}>{isEditing ? 'Редагувати запис' : 'Створити запис'}</h3>
          <Button className={styles.closeForm} onClick={() => setIsOpen(false)} noStyle>
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </div>

        <div className={styles.formBody}>
          <div className={styles.column}>
            <TimeRangePicker />
            <EntryTypeSelector />
            <GroupInput />
            <HallSelector />
          </div>

          <div className={styles.formRow}>
            <ClientFields />
            <CoachFields />
          </div>
        </div>

        <CommentInput />

        <div className={styles.buttons}>
          {error?.details && (
            <div className={styles.errorMessage}>
              <FontAwesomeIcon className={styles.errorIcon} icon={faExclamationTriangle} />
              <p>
                <span>{error?.error}</span>
                {error?.details}
              </p>
            </div>
          )}
          <Button className={styles.formBtn} text='Скинути' type='button' onClick={form.resetForm} ghostStyle />
          <Button
            className={styles.formBtn}
            text={isEditing ? 'Оновити' : 'Створити'}
            type='submit'
            disabled={form.isSubmitting || !form.isValid}
          />
        </div>
      </form>
    </EntryFormProvider>
  );
};
